import axios from 'axios';

// In production builds (NODE_ENV=production) always use relative URLs so the
// correct https:// scheme is inherited automatically from the page origin.
// In local dev use REACT_APP_API_URL or fall back to localhost.
const API_URL = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:8010');

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('npz_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    // A wrong password on the login form itself must stay on the page so the
    // error is visible (redirecting to /login would reload and wipe it).
    if (err.response?.status === 401 && !err.config?.url?.includes('/api/auth/login')) {
      const detail = err.response?.data?.detail || '';
      // Only force-logout for real JWT failures, not "Fyers not connected" type 401s
      const isAuthFailure =
        detail.toLowerCase().includes('invalid token') ||
        detail.toLowerCase().includes('could not validate') ||
        detail.toLowerCase().includes('not authenticated') ||
        err.config?.url?.includes('/api/auth/');
      if (isAuthFailure) {
        localStorage.removeItem('npz_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export const login = (username, password) =>
  api.post('/api/auth/login', { username, password });

export const getStats = () => api.get('/api/dashboard/stats');
export const getEquityCurve = () => api.get('/api/dashboard/equity-curve');
export const getIntradayPnl = () => api.get('/api/dashboard/intraday-pnl');
export const getActivity = () => api.get('/api/dashboard/activity');
export const getTrades = (page = 1) => api.get(`/api/dashboard/trades?page=${page}`);
export const setKillSwitch = (active) => api.post('/api/dashboard/kill-switch', { active });

export const getPositions = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return api.get(`/api/positions${q ? '?' + q : ''}`);
};
export const exitPosition     = (id)     => api.delete(`/api/positions/${id}`);
export const exitLivePosition = (symbol) => api.delete(`/api/positions/live/${encodeURIComponent(symbol)}`);
export const exitAllPositions = ()       => api.delete('/api/positions/');

export const getOrders = () => api.get('/api/orders');
export const placeOrder = (data) => api.post('/api/orders/place', data);
export const cancelOrder = (id) => api.delete(`/api/orders/${id}`);

export const getStrategies = () => api.get('/api/strategies');
export const toggleStrategy = (name, enabled) =>
  api.put(`/api/strategies/${name}/toggle`, { enabled });
export const updateStrategyConfig = (name, params) =>
  api.put(`/api/strategies/${name}/config`, params);
export const togglePaperTrading = (enabled) =>
  api.post('/api/strategies/paper-trading', { enabled });
export const getStrategyPerformance = () => api.get('/api/strategies/performance');

// Price Alerts
export const getAlerts = (activeOnly = false) =>
  api.get(`/api/alerts${activeOnly ? '?active_only=true' : ''}`);
export const createAlert = (data) => api.post('/api/alerts', data);
export const deleteAlert = (id) => api.delete(`/api/alerts/${id}`);
export const deactivateAlert = (id) => api.post(`/api/alerts/${id}/deactivate`);

// Trade Journal
export const getJournalEntries = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return api.get(`/api/journal${q ? '?' + q : ''}`);
};
export const createJournalEntry = (data) => api.post('/api/journal', data);
export const updateJournalEntry = (id, data) => api.patch(`/api/journal/${id}`, data);
export const deleteJournalEntry = (id) => api.delete(`/api/journal/${id}`);
export const getJournalStats = () => api.get('/api/journal/stats/summary');

// News
export const getNews = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return api.get(`/api/news${q ? '?' + q : ''}`);
};
export const getTrendingStocks   = (top = 10) => api.get(`/api/news/trending?top=${top}`);
export const getNewsSentiment    = ()          => api.get('/api/news/sentiment');

export const getFyersAuthUrl    = ()           => api.get('/api/auth/fyers/auth-url');
export const getFyersStatus     = ()           => api.get('/api/auth/fyers/status');
export const completeFyersAuth  = (auth_code)  => api.post('/api/auth/fyers/callback', { auth_code });
export const saveFyersToken     = (token)      => api.post('/api/auth/fyers/token', { token });
export const clearFyersToken    = ()           => api.delete('/api/auth/fyers/token');
export const startEngine        = ()           => api.post('/api/engine/start');

// Risk
export const getRiskStatus    = ()       => api.get('/api/risk/status');
export const updateRiskConfig = (cfg)    => api.post('/api/risk/config', cfg);
export const getMargin        = ()       => api.get('/api/risk/margin');

// App Logs
export const getLogs = (params = {}) => {
  const q = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null))
  ).toString();
  return api.get(`/api/logs${q ? '?' + q : ''}`);
};
export const clearOldLogs = (olderThanDays = 7) =>
  api.delete(`/api/logs?older_than_days=${olderThanDays}`);

export const createWebSocket = (path) => {
  const wsBase = API_URL.replace('http', 'ws');
  return new WebSocket(`${wsBase}${path}`);
};

export { api as apiClient };
export default api;

// ── Swing Trader ─────────────────────────────────────────────────────────────
export const getSwingSettings    = () => api.get('/api/swing/settings');
export const updateSwingSettings = (data) => api.put('/api/swing/settings', data);
export const runSwingScan        = () => api.post('/api/swing/scan/run');
export const getSwingScanStatus  = () => api.get('/api/swing/scan/status');
export const getSwingScanResults = (params = {}) => api.get('/api/swing/scan/results', { params });
export const getSwingSymbolScan  = (symbol, params = {}) => api.get(`/api/swing/scan/results/${symbol}`, { params });
export const generateSwingPortfolio = (data) => api.post('/api/swing/portfolio/generate', data);
export const applySwingPortfolio    = (data) => api.post('/api/swing/portfolio/apply', data);
export const getSwingPositions   = (params = {}) => api.get('/api/swing/positions', { params });
export const createSwingPosition = (data) => api.post('/api/swing/positions', data);
export const updateSwingPosition = (id, data) => api.patch(`/api/swing/positions/${id}`, data);
export const deleteSwingPosition = (id) => api.delete(`/api/swing/positions/${id}`);
export const getSwingLivePrices  = (symbols) => api.get('/api/swing/positions/live-prices', { params: { symbols: symbols.join(',') } });
export const getSwingAlerts      = (params = {}) => api.get('/api/swing/alerts', { params });
export const ackSwingAlert       = (id) => api.post(`/api/swing/alerts/${id}/ack`);
export const dismissSwingAlert   = (id) => api.delete(`/api/swing/alerts/${id}`);
export const runSwingRebalance   = (params = {}) => api.post('/api/swing/rebalance/run', null, { params });
export const applySwingRebalance = (report) => api.post('/api/swing/rebalance/apply', report);
export const getSwingSchedulerStatus = () => api.get('/api/swing/scheduler/status');
export const triggerSwingEOD     = () => api.post('/api/swing/scheduler/eod/trigger');

// ── AI Trading ───────────────────────────────────────────────────────────────
export const getAIStatus   = () => api.get('/api/ai/status');
export const enableAI      = () => api.post('/api/ai/enable');
export const disableAI     = () => api.post('/api/ai/disable');
export const setAIPaper    = (enabled) => api.post('/api/ai/paper', { enabled });
export const getAIConfig   = () => api.get('/api/ai/config');
export const updateAIConfig = (params) => api.post('/api/ai/config', { params });
export const resetAIConfig  = () => api.post('/api/ai/config/reset');
export const runAIBacktest = (body) => api.post('/api/ai/backtest', body);

// ── Brokers (multi-broker Phase 1) ───────────────────────────────────────────
export const getBrokers      = () => api.get('/api/brokers');
export const setActiveBroker = (broker_id) => api.post('/api/brokers/active', { broker_id });
